/**
 * End-of-simulation result screen.
 *
 * Receives the submit response via React Router location state. Shows:
 *   - Pass/fail headline with score (X/Y correct, threshold).
 *   - Wrong-answer review: every wrong card rendered with EVERYTHING expanded
 *     (German + English question, all 4 options, correct highlighted, user's
 *     pick highlighted in red, tip auto-revealed). Per the locked design
 *     (Q15) — sim review is a teaching moment, so we don't hide anything.
 *   - Single CTA: "Back to home".
 *
 * If the user navigated here directly (no state), redirect to app home.
 */
import { Check, Trophy, X } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';

import { QuestionImage } from '@/apps/einbuergerungstest/QuestionImage';
import { Button } from '@/components/ui/button';
import type { Card, EinbuergerungsPayload, OptionLetter } from '@/core/types';
import { cn } from '@/lib/utils';

import type { SimulationResultLocationState } from './simulation';

export function SimulationResultRoute() {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as SimulationResultLocationState | null;

  // Defend against direct navigation: nothing to render without a result.
  useEffect(() => {
    if (!state?.result) navigate(`/apps/${appId}`, { replace: true });
  }, [state, appId, navigate]);

  if (!state?.result) return null;

  const { result } = state;
  const { passed, correct_count, total_questions, pass_threshold, wrong_cards, answers } = result;
  const accuracy = total_questions > 0 ? correct_count / total_questions : 0;
  const answerByCard = new Map(answers.map((a) => [a.card_id, a]));

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border bg-card border-b">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-6">
          <h1 className="font-display text-xl font-semibold">Simulation result</h1>
        </div>
      </header>

      <main className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6 sm:px-6 sm:py-10">
        <section
          className={cn(
            'rounded-2xl border-2 p-6 text-center',
            passed ? 'border-success/40 bg-success/5' : 'border-destructive/40 bg-destructive/5',
          )}
        >
          {passed ? (
            <Trophy className="text-success mx-auto size-10" />
          ) : (
            <X className="text-destructive mx-auto size-10" />
          )}
          <h2 className="font-display mt-3 text-3xl font-semibold">
            {passed ? 'Passed' : 'Not yet'}
          </h2>
          <p className="mt-2 text-base">
            <span className="font-display text-2xl font-semibold">
              {correct_count}/{total_questions}
            </span>{' '}
            correct · {Math.round(accuracy * 100)}%
          </p>
          <p className="text-muted-foreground mt-1 text-sm">Pass threshold: {pass_threshold}</p>
        </section>

        {wrong_cards.length > 0 ? (
          <section className="flex flex-col gap-3">
            <h3 className="text-muted-foreground text-sm font-medium">
              Review {wrong_cards.length} {wrong_cards.length === 1 ? 'question' : 'questions'} you
              got wrong
            </h3>
            <div className="flex flex-col gap-4">
              {wrong_cards.map((card) => (
                <WrongCardReview
                  key={card.id}
                  card={card}
                  userLetter={
                    (answerByCard.get(card.id)?.user_letter ?? null) as OptionLetter | null
                  }
                />
              ))}
            </div>
          </section>
        ) : (
          <p className="text-muted-foreground text-center text-sm">
            All answers correct. Strong work.
          </p>
        )}

        <div className="sticky bottom-4 mt-2">
          <Button asChild size="lg" className="w-full">
            <Link to={`/apps/${appId}`}>Back to home</Link>
          </Button>
        </div>
      </main>
    </div>
  );
}

function WrongCardReview({
  card,
  userLetter,
}: {
  card: Card<EinbuergerungsPayload>;
  userLetter: OptionLetter | null;
}) {
  return (
    <article className="border-border bg-card rounded-2xl border p-5">
      {card.external_id ? (
        <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
          Question {card.external_id}
          {card.payload.scope === 'state' && card.payload.state_code
            ? ` · ${card.payload.state_code}`
            : ''}
        </p>
      ) : null}
      <h4 className="font-display mt-1 text-lg leading-snug font-semibold">{card.de}</h4>
      <p className="text-muted-foreground mt-1 text-sm italic">{card.payload.question_en}</p>

      {card.payload.has_image && card.external_id ? (
        <div className="mt-3">
          <QuestionImage bamfId={card.external_id} size="sm" />
        </div>
      ) : null}

      <ul className="mt-4 flex flex-col gap-1.5">
        {card.payload.options.map((opt) => {
          const isCorrect = opt.letter === card.payload.correct_letter;
          const isUserPick = opt.letter === userLetter;
          const ring = isCorrect
            ? 'border-success bg-success/10'
            : isUserPick
              ? 'border-destructive bg-destructive/10'
              : 'border-border bg-card opacity-70';
          const letterBg = isCorrect
            ? 'bg-success text-success-foreground'
            : isUserPick
              ? 'bg-destructive text-destructive-foreground'
              : 'bg-muted text-foreground';
          return (
            <li
              key={opt.letter}
              className={cn('flex items-start gap-3 rounded-md border p-3 text-sm', ring)}
            >
              <span
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-semibold',
                  letterBg,
                )}
              >
                {opt.letter}
              </span>
              <span className="flex-1">
                <span className="block">{opt.de}</span>
                <span className="text-muted-foreground mt-0.5 block text-xs italic">{opt.en}</span>
              </span>
              {isCorrect ? <Check className="text-success size-4" /> : null}
              {isUserPick && !isCorrect ? <X className="text-destructive size-4" /> : null}
            </li>
          );
        })}
      </ul>

      {card.payload.tip_en ? (
        <p className="border-primary/30 bg-primary/5 mt-3 rounded-md border p-3 text-sm">
          <span className="font-medium">Tip: </span>
          {card.payload.tip_en}
        </p>
      ) : null}
    </article>
  );
}
